import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../features.css";
import API from "../../utils/API";
import { storage } from "../../config/Fire";
import { FEATURES } from "../../config/features";
import { useAuth } from "../../auth/AuthProvider";
import { useBookData } from "../../data/BookDataProvider";
import {
  Button,
  Card,
  ChipToggle,
  Dropzone,
  Field,
  Input,
  MiniTimeline,
  Select,
  TextArea,
  useToast,
  CheckIcon
} from "../../ui";

/**
 * Referral submit (/app/refer) — REFERRAL-UX-SPEC §4. One card, one
 * column, five groups; a 90-second act of goodwill, not paperwork.
 *
 * Duplicate-check (ratified contract): POST /api/prospects/duplicate-check
 * → {match: "none"|"own"|"other", ...minimal own fields}. Variant A (own
 * book) names the match; Variant B (elsewhere) NEVER identifies whose
 * client it is — a security requirement, not a nicety. If the endpoint
 * isn't live yet (404/network), the form degrades to a direct submit.
 *
 * Dec-page uploads (§3c) are behind FEATURES.decUpload (default OFF — the
 * whole group is hidden, no dead affordance) until the prospect-attachment
 * contract ratifies. The renewal-date field always ships: it's the nudge
 * engine's best fuel.
 */

const LOBS = [
  { value: "Auto", label: "Auto" },
  { value: "Home", label: "Home" },
  { value: "Umbrella", label: "Umbrella" },
  { value: "Other", label: "Other" }
];

const STATES =
  "AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC".split(
    " "
  );

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  city: "",
  state: "PA",
  phone: "",
  email: "",
  lines: [],
  renewalDate: "",
  notes: ""
};

function ReferPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { identity } = useAuth();
  const { refresh } = useBookData();

  const [form, setForm] = useState(EMPTY_FORM);
  const [files, setFiles] = useState([]); // { id, file, name, size, status }
  const [errors, setErrors] = useState({});
  const [duplicate, setDuplicate] = useState(null); // {match, name?, referredAt?} once checked
  const [submitting, setSubmitting] = useState(false);
  const [sentName, setSentName] = useState(null);
  const [sentRetention, setSentRetention] = useState(false);

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  /* Dec pages — the ruled v1 binary path (Coordinator, 2026-07-11): the
     browser uploads to Firebase Storage IMMEDIATELY on add (E §4: upload in
     the background, submit only blocks on in-flight); the metadata POST to
     /api/documents/prospect/:id happens after the prospect exists, and the
     backend derives owner + uploadedBy server-side. A failed upload marks
     the chip "failed" and is simply not attached — visible, never silent.
     (Local harness note: emulator-issued tokens can't write to the real
     bucket, so uploads fail visibly in dev; the metadata leg verifies
     against the stub, the binary leg at the Storage-rules deploy gate.) */
  const addFiles = (picked) => {
    const entries = picked.map((file) => ({
      id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      name: file.name,
      size: file.size,
      status: "uploading",
      url: null
    }));
    setFiles((existing) => [...existing, ...entries]);
    entries.forEach((entry) => {
      storage
        .ref(`decpages/${Date.now()}-${entry.name}`)
        .put(entry.file)
        .then((snap) => snap.ref.getDownloadURL())
        .then((url) =>
          setFiles((fs) => fs.map((f) => (f.id === entry.id ? { ...f, status: "done", url } : f)))
        )
        .catch(() =>
          setFiles((fs) => fs.map((f) => (f.id === entry.id ? { ...f, status: "error" } : f)))
        );
    });
  };

  const uploadsInFlight = files.some((f) => f.status === "uploading");

  const validate = () => {
    const next = {};
    if (!form.firstName.trim()) {
      next.firstName = "First name is required.";
    }
    if (!form.lastName.trim()) {
      next.lastName = "Last name is required.";
    }
    if (!form.phone.trim() && !form.email.trim()) {
      next.contact = "Give us a phone or an email so Evan and Randy can follow up.";
    }
    if (form.email.trim() && !/.+@.+\..+/.test(form.email.trim())) {
      next.email = "That email address doesn't look right.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = (possibleDuplicate, retentionClientRef) => {
    const payload = {
      name: `${form.firstName.trim()} ${form.lastName.trim()}`,
      telephone: form.phone.trim() || undefined,
      address:
        [form.city.trim(), form.city.trim() ? form.state : ""].filter(Boolean).join(", ") ||
        undefined,
      lines: form.lines.join(", ") || undefined,
      notes: form.notes.trim() || undefined,
      renewalDate: form.renewalDate || undefined,
      referrerID: identity ? String(identity._id) : undefined,
      possibleDuplicate: possibleDuplicate || undefined
    };
    // email is unique+sparse on Prospect — never send an empty string.
    if (form.email.trim()) {
      payload.email = form.email.trim();
    }
    // Retention case (ROLE-DESIGN §10.2): additive fields on the SAME create
    // route — the backend validates the client is in the caller's book
    // (Coordinator/Session-I 2026-07-12). Bound later attaches to this client;
    // there is no convert flow.
    if (retentionClientRef) {
      payload.caseType = "retention";
      payload.clientRef = retentionClientRef;
    }
    return payload;
  };

  const createProspect = async (possibleDuplicate, retentionClientRef) => {
    setSubmitting(true);
    try {
      const res = await API.saveAgentProspectSubmission(
        identity ? String(identity._id) : "self",
        buildPayload(possibleDuplicate, retentionClientRef)
      );
      const prospect = res.data || {};
      const uploaded = files.filter((f) => f.status === "done" && f.url);
      if (uploaded.length > 0 && prospect._id) {
        // Shipped contract (H T3): metadata attach; owner/uploadedBy are
        // server-derived, so the body carries only the file facts.
        await Promise.allSettled(
          uploaded.map((f) =>
            API.createDocumentForProspect(prospect._id, {
              docName: f.name,
              document: f.url,
              type: "Declarations"
            })
          )
        );
      }
      setSentName(form.firstName.trim() ? `the ${form.lastName.trim()} household` : "them");
      setSentRetention(Boolean(retentionClientRef));
      refresh();
    } catch (err) {
      toast.error("The referral didn't send — nothing was lost, try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (!validate()) {
      return;
    }
    if (duplicate) {
      // Second press = "Submit anyway" path handled by the panel buttons.
      return;
    }
    setSubmitting(true);
    let check = { match: "none" };
    try {
      const res = await API.checkProspectDuplicate({
        name: `${form.firstName.trim()} ${form.lastName.trim()}`,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined
      });
      if (res.data && ["own", "other"].includes(res.data.match)) {
        check = res.data;
      }
    } catch (err) {
      // Endpoint not live yet (or transient) — degrade to a direct submit.
    }
    if (check.match === "none") {
      await createProspect(false);
    } else {
      setDuplicate(check);
      setSubmitting(false);
    }
  };

  // A duplicate "own" hit whose first match is an existing CLIENT (not a
  // prospect) is the retention third-path trigger (ROLE-DESIGN §10.2).
  const firstMatch = duplicate && duplicate.ownMatches && duplicate.ownMatches[0];
  const isExistingClient = Boolean(
    duplicate &&
    duplicate.match === "own" &&
    firstMatch &&
    firstMatch.kind === "client" &&
    firstMatch._id
  );

  if (sentName) {
    return (
      <div className="shell-page feat-home">
        <Card>
          <div className="feat-refer-success">
            <span className="feat-refer-success__check">
              <CheckIcon size={28} />
            </span>
            <h2 className="feat-refer-success__title">
              {sentRetention ? "Retention case opened!" : "Referral sent!"}
            </h2>
            <p className="feat-refer-success__line">
              {sentRetention
                ? `We'll re-quote ${sentName} and keep you posted — this stays attached to their existing account, nothing to re-create.`
                : `We'll take it from here — you'll get an email when ${sentName} gets quoted, and at every milestone after.`}
            </p>
            <MiniTimeline
              steps={[
                { label: "Gathering info", done: true },
                { label: "Quoting", done: false },
                { label: "Presented", done: false }
              ]}
            />
            <div className="feat-refer-success__actions">
              <Button
                variant="primary"
                onClick={() => {
                  setSentName(null);
                  setSentRetention(false);
                  setForm(EMPTY_FORM);
                  setFiles([]);
                  setDuplicate(null);
                }}
              >
                Refer another household
              </Button>
              <Button variant="ghost" onClick={() => navigate("/app/home")}>
                Back to home
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="shell-page feat-home">
      <div className="shell-page__titlerow">
        <h1 className="shell-page__title">Refer a household</h1>
      </div>

      <Card>
        <div className="feat-refer-form">
          <h3 className="feat-kv-section__title">Who are they?</h3>
          <div className="feat-refer-row">
            <Field label="First name" required error={errors.firstName} htmlFor="rf-first">
              <Input
                id="rf-first"
                value={form.firstName}
                onChange={set("firstName")}
                error={Boolean(errors.firstName)}
                autoComplete="off"
              />
            </Field>
            <Field label="Last name" required error={errors.lastName} htmlFor="rf-last">
              <Input
                id="rf-last"
                value={form.lastName}
                onChange={set("lastName")}
                error={Boolean(errors.lastName)}
                autoComplete="off"
              />
            </Field>
          </div>
          <div className="feat-refer-row feat-refer-row--citystate">
            <Field label="City" htmlFor="rf-city">
              <Input id="rf-city" value={form.city} onChange={set("city")} autoComplete="off" />
            </Field>
            <Field label="State" htmlFor="rf-state">
              <Select id="rf-state" value={form.state} onChange={set("state")}>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <h3 className="feat-kv-section__title">How do we reach them?</h3>
          {errors.contact ? <p className="fk-field__error">{errors.contact}</p> : null}
          <div className="feat-refer-row">
            <Field label="Phone" htmlFor="rf-phone">
              <Input
                id="rf-phone"
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                autoComplete="off"
              />
            </Field>
            <Field label="Email" error={errors.email} htmlFor="rf-email">
              <Input
                id="rf-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                error={Boolean(errors.email)}
                autoComplete="off"
              />
            </Field>
          </div>

          <h3 className="feat-kv-section__title">What are they interested in?</h3>
          <ChipToggle
            aria-label="Lines of business"
            options={LOBS}
            value={form.lines}
            onChange={(lines) => setForm((f) => ({ ...f, lines }))}
          />

          <h3 className="feat-kv-section__title" style={{ marginTop: "var(--space-5)" }}>
            Current policies <span className="fk-muted">(optional)</span>
          </h3>
          <p className="feat-cell-secondary" style={{ margin: "0 0 var(--space-3)" }}>
            Their current dec pages help us time quotes around their renewal.
          </p>
          <div style={{ marginBottom: "var(--space-4)" }}>
            <Dropzone
              label="Add dec pages — photo or PDF"
              accept="image/*,.pdf"
              capture="environment"
              files={files}
              onAdd={addFiles}
              onRemove={(id) => setFiles((fs) => fs.filter((f) => f.id !== id))}
            />
          </div>
          <Field label="When does their current policy renew?" htmlFor="rf-renewal">
            <Input
              id="rf-renewal"
              type="date"
              value={form.renewalDate}
              onChange={set("renewalDate")}
            />
          </Field>

          <h3 className="feat-kv-section__title">Anything else?</h3>
          <Field htmlFor="rf-notes" label="Notes">
            <TextArea
              id="rf-notes"
              rows={3}
              value={form.notes}
              onChange={set("notes")}
              placeholder="How you know them, timing, anything useful."
            />
          </Field>

          {duplicate ? (
            <div className="feat-dup-panel">
              {duplicate.match === "own" ? (
                /* Shipped shape (integration-verified): own matches arrive as
                   an ownMatches[] array of {_id, name, kind, pipelineStage} —
                   the panel presents the first. */
                <>
                  <p className="feat-dup-panel__text">
                    {isExistingClient ? (
                      FEATURES.retention ? (
                        <>
                          This household is already a client:{" "}
                          <strong>{duplicate.ownMatches[0].name}</strong>. Want to start a{" "}
                          <strong>retention case</strong> — a fresh re-quote that stays on their
                          existing account?
                        </>
                      ) : (
                        <>
                          This looks like an existing client:{" "}
                          <strong>{duplicate.ownMatches[0].name}</strong>. Open their record — or
                          submit anyway if this is a different person.
                        </>
                      )
                    ) : (
                      <>
                        This looks like someone already in your book:{" "}
                        <strong>
                          {(duplicate.ownMatches && duplicate.ownMatches[0]?.name) ||
                            "an existing record"}
                        </strong>
                        .
                      </>
                    )}
                  </p>
                  <div className="feat-dup-panel__actions">
                    {isExistingClient && FEATURES.retention ? (
                      <Button
                        variant="primary"
                        loading={submitting}
                        onClick={() => createProspect(false, String(duplicate.ownMatches[0]._id))}
                      >
                        Start a retention case
                      </Button>
                    ) : null}
                    {isExistingClient ? (
                      <Button
                        variant="secondary"
                        onClick={() => navigate(`/app/clients/${duplicate.ownMatches[0]._id}`)}
                      >
                        View the record
                      </Button>
                    ) : null}
                    {/* E QA MED: don't offer "Submit anyway" for an existing
                        CLIENT when the retention path is available — creating a
                        duplicate prospect competes with it. But when retention
                        is OFF (default), keep "Submit anyway" as the escape for
                        a name-collision false positive, or nothing could be
                        filed. (Prospect-kind matches always keep it.) */}
                    {!(isExistingClient && FEATURES.retention) ? (
                      <Button
                        variant="primary"
                        loading={submitting}
                        onClick={() => createProspect(true)}
                      >
                        Submit anyway
                      </Button>
                    ) : null}
                    <Button variant="ghost" onClick={() => setDuplicate(null)}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="feat-dup-panel__text">
                    We may already know this household — we'll double-check on our side so nothing
                    gets tangled.
                  </p>
                  <div className="feat-dup-panel__actions">
                    <Button
                      variant="primary"
                      loading={submitting}
                      onClick={() => createProspect(true)}
                    >
                      Submit anyway
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : null}

          {!duplicate ? (
            <Button
              variant="primary"
              loading={submitting || uploadsInFlight}
              onClick={submit}
              style={{ width: "100%" }}
            >
              {uploadsInFlight ? "Uploading dec pages…" : "Send referral"}
            </Button>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

export default ReferPage;
